import { GameRouteService } from "../services/GameRouteService";
import { css } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";

/** CSS affecting the {@link CutsceneComponent} */
const styles: string = css`
    :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 10000;
        background: black;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }
    .cutscene-video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        opacity: 0;
        transition: opacity 0.5s ease;
    }

    .fade-in {
        opacity: 1 !important;
    }

    .fade-out {
        opacity: 0 !important;
    }
    .skip-button {
        position: absolute;
        bottom: 20px;
        right: 20px;
        padding: 10px 20px;
        background-color: rgba(0, 0, 0, 0.7);
        color: white;
        border: 1px solid white;
        border-radius: 4px;
        font-family: inherit;
        font-size: 14px;
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 0.2s ease;
        z-index: 10001;
    }
    .skip-button:hover {
        opacity: 1;
        background-color: rgba(50, 50, 50, 0.8);
    }
    .skip-button:active {
        transform: scale(0.98);
    }
`;

export class CutsceneComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    private cutsceneVideo!: HTMLVideoElement;
    private skipCutsceneBtn!: HTMLButtonElement;
    private _cutsceneNumber = 0;

    public static get observedAttributes(): string[] {
        return ["cutscene-number"];
    }

    public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === "cutscene-number" && oldValue !== newValue) {
            this._cutsceneNumber = parseInt(newValue) || 0;
        }
    }

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
        this.loadEventListeners();
        if (this._cutsceneNumber > 0) {
            this.loadCutscene();
        }
    }

    private render(): void {
        if (!this.shadowRoot) return;

        const style: HTMLStyleElement = document.createElement("style");
        style.textContent = styles;
        this.shadowRoot.appendChild(style);

        const container: HTMLDivElement = document.createElement("div");
        container.className = "container";

        this.cutsceneVideo = document.createElement("video");
        this.cutsceneVideo.className = "cutscene-video";
        this.cutsceneVideo.autoplay = true;
        this.cutsceneVideo.playsInline = true;
        this.cutsceneVideo.muted = false;

        this.skipCutsceneBtn = document.createElement("button");
        this.skipCutsceneBtn.type = "button";
        this.skipCutsceneBtn.className = "skip-button";
        this.skipCutsceneBtn.textContent = "Press Space to Skip";

        container.appendChild(this.cutsceneVideo);
        container.appendChild(this.skipCutsceneBtn);
        this.shadowRoot.appendChild(container);

        this._gameEventService.StopAudio();
    }

    public loadEventListeners(): void {
        window.addEventListener("keydown", async event => {
            if (event.key === "Space" || event.key === " " || event.key === "Spacebar") {
                event.preventDefault();
                await this.handleVideoEnd();
            }
        });

        this.skipCutsceneBtn.addEventListener("click", async () => {
            await this.handleVideoEnd();
        });
    }

    private loadCutscene(): void {
        if (!this._cutsceneNumber) {
            this.dispatchEndEvent();
            return;
        }

        const videoPath: string = this.getVideoPath(this._cutsceneNumber);
        this.cutsceneVideo.src = `/assets/img/cutscenes/${videoPath}`;

        // Clear previous event listeners
        this.cutsceneVideo.onended = null;
        this.cutsceneVideo.onerror = null;

        this.cutsceneVideo.oncanplay = () => {
            this.cutsceneVideo.classList.add("fade-in");
        };
        this.cutsceneVideo.onended = async () => {
            this.cutsceneVideo.classList.remove("fade-in");
            this.cutsceneVideo.classList.add("fade-out");
            await new Promise(resolve => setTimeout(resolve, 500));
            await this.handleVideoEnd();
        };
        this.cutsceneVideo.onerror = async () => {
            console.error(`Failed to load cutscene ${this._cutsceneNumber} AKA ${videoPath}`);
            await this.handleVideoEnd();
        };
    }

    private async handleVideoEnd(): Promise<void> {
        this.dispatchEndEvent();
        await this.handlePostCutsceneActions();
        this.remove(); // Remove self from DOM
    }

    private dispatchEndEvent(): void {
        if (this._cutsceneNumber !== 4 && this._cutsceneNumber !== 5 && this._cutsceneNumber !== 6) {
            this._gameEventService.endCutscene();
        }
    }

    private getVideoPath(cutsceneNumber: number): string {
        const cutsceneMap: Record<number, string> = {
            1: "bodysmack.mp4",
            2: "bloodpunch.mp4",
            3: "backslap.mp4",
            4: "failedtododge.mp4",
            5: "successtododge.mp4",
            6: "successtodefeat.mp4",
            7: "proper_eating.mp4",
            8: "scary_eating.mp4",
            9: "airplane-crash.mp4",
            10: "trigger-warning.mp4",
            11: "zombie-eating.mp4",
        };
        return cutsceneMap[cutsceneNumber] || "funny.mp4";
    }

    private async handlePostCutsceneActions(): Promise<void> {
        // Only update state if absolutely necessary
        if (this._cutsceneNumber === 9) {
            await this._gameRouteService.afterPlaneCrash();
        }
        // Consider whether this is always needed
        if (this._cutsceneNumber !== 4 && this._cutsceneNumber !== 5 && this._cutsceneNumber !== 6) {
            await this._gameRouteService.clearCutscene();
        }
    }
}
